// src/lib/api-logger.ts
function createApiLogger(isDev) {
  return {
    onError: (method, url, status, durationMs, error) => {
      console.error(
        `[API] ${method.toUpperCase()} ${url} -> ${status ?? "NETWORK_ERROR"} (${Math.round(durationMs)}ms)`,
        error
      );
    },
    onRequest: (method, url) => {
      if (isDev) console.debug(`[API] ${method.toUpperCase()} ${url}`);
    },
    onResponse: (method, url, status, durationMs) => {
      if (isDev) console.debug(`[API] ${method.toUpperCase()} ${url} -> ${status} (${Math.round(durationMs)}ms)`);
    }
  };
}

// src/lib/date-utils.ts
import { format } from "date-fns/format";
import { isSameDay } from "date-fns/isSameDay";
function formatDisplayDate(date) {
  return format(date, "PPpp");
}
function formatIsoDate(date) {
  return date.toISOString();
}
function formatShortDate(date) {
  return format(date, "dd/MM/yyyy");
}
function formatShortDateRange(from, to) {
  return `${formatShortDate(from)} \u2014 ${formatShortDate(to)}`;
}
function formatTimeWindow(window) {
  const start = new Date(window.start);
  const end = new Date(window.end);
  return isSameDay(start, end) ? `${format(start, "dd/MM")} ${format(start, "HH:mm")}-${format(end, "HH:mm")}` : `${format(start, "dd/MM")} ${format(start, "HH:mm")} \u2192 ${format(end, "dd/MM")} ${format(end, "HH:mm")}`;
}

// src/lib/locale-date.ts
var LOCALE_MAP = {
  ar: "ar-SA",
  en: "en-US",
  fr: "fr-FR"
};
function resolveLocale(language) {
  return LOCALE_MAP[language] ?? "en-US";
}
function formatLongDate(language) {
  return (/* @__PURE__ */ new Date()).toLocaleDateString(resolveLocale(language), {
    day: "numeric",
    month: "long",
    weekday: "long",
    year: "numeric"
  });
}
function formatDateTimeMedium(date, language) {
  if (!date) return "\u2014";
  return new Intl.DateTimeFormat(resolveLocale(language), {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(date));
}

// src/lib/pagination-helpers.ts
function toQueryParams(params) {
  const query = {
    limit: params.pageSize,
    page: params.page
  };
  if (params.sort) {
    query.sort = params.sort.sortBy;
    query.sortDirection = params.sort.sortDirection;
  }
  return query;
}
function mapPaginatedResponse(response) {
  return {
    items: response.items,
    page: response.page,
    pageSize: response.pageSize,
    total: response.total,
    totalPages: response.totalPages
  };
}
var toPaginatedResponse = mapPaginatedResponse;
function buildSortParams(sortBy, sortDirection) {
  return sortDirection === null ? void 0 : { sortBy, sortDirection };
}

// src/lib/relative-date.ts
function formatRelativeDate(date, labels) {
  if (!date) return labels.never;
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 6e4);
  const diffHours = Math.floor(diffMs / 36e5);
  const diffDays = Math.floor(diffMs / 864e5);
  if (diffMins < 1) return labels.justNow;
  if (diffMins < 60) return labels.minutesAgo(diffMins);
  if (diffHours < 24) return labels.hoursAgo(diffHours);
  if (diffDays < 7) return labels.daysAgo(diffDays);
  return formatDisplayDate(new Date(date));
}

// src/lib/password-requirements.ts
import {
  PASSWORD_LOWERCASE_REGEX,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_NUMBER_REGEX,
  PASSWORD_SYMBOL_REGEX,
  PASSWORD_UPPERCASE_REGEX
} from "@hetivo/hetivo-lib-types";
function getPasswordRequirements(password) {
  return [
    { isMet: password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH, key: "length" },
    { isMet: PASSWORD_UPPERCASE_REGEX.test(password), key: "uppercase" },
    { isMet: PASSWORD_LOWERCASE_REGEX.test(password), key: "lowercase" },
    { isMet: PASSWORD_NUMBER_REGEX.test(password), key: "number" },
    { isMet: PASSWORD_SYMBOL_REGEX.test(password), key: "symbol" }
  ];
}
function areAllRequirementsMet(password) {
  return getPasswordRequirements(password).every((r) => r.isMet);
}

// src/index.ts
import { isApiError, getApiData } from "@hetivo/hetivo-lib-utils";

// src/constants.ts
var DEFAULT_GC_TIME_MS = 10 * 60 * 1e3;
var DEFAULT_STALE_TIME_MS = 5 * 60 * 1e3;
var RTL_LANGUAGES = ["ar", "he", "fa"];
var LANGUAGE_LABELS = {
  ar: "\u0627\u0644\u0639\u0631\u0628\u064A\u0629",
  en: "English",
  fr: "Fran\xE7ais"
};
var DEFAULT_PAGE_SIZE = 10;
var SEARCH_DEBOUNCE_MS = 300;

// src/hooks/use-current-user-permissions.ts
import { useQuery } from "@tanstack/react-query";

// src/api/api-client.ts
import axios from "axios";

// src/api/api-error.ts
var ApiError = class extends Error {
  detail;
  constructor(detail) {
    super(detail.message);
    this.name = "ApiError";
    this.detail = detail;
  }
};
function generateCorrelationId(prefix) {
  const uuid = generateUuid();
  return `${prefix}:${uuid}`;
}
function getApiErrorDetail(error) {
  if (error instanceof ApiError) {
    return error.detail;
  }
  if (isAxiosError(error)) {
    return extractFromAxiosError(error);
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: "An unknown error occurred" };
}
function generateUuid() {
  try {
    return crypto.randomUUID();
  } catch {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
}
function isAxiosError(error) {
  return error !== null && typeof error === "object" && error.isAxiosError === true;
}
function extractFromAxiosError(error) {
  const data = error.response?.data;
  const status = error.response?.status;
  if (!data || typeof data !== "object") {
    return {
      message: error.message ?? "Request failed",
      statusCode: status
    };
  }
  return buildDetailFromResponseData(data, error.message, status);
}
function buildDetailFromResponseData(data, fallbackMessage, status) {
  const message = typeof data["message"] === "string" ? data["message"] : fallbackMessage ?? "Request failed";
  const statusCode = typeof data["statusCode"] === "number" ? data["statusCode"] : status;
  const correlationId = typeof data["correlationId"] === "string" ? data["correlationId"] : void 0;
  const errorCode = typeof data["errorCode"] === "string" ? data["errorCode"] : void 0;
  const conflictingRecordName = typeof data["conflictingRecordName"] === "string" ? data["conflictingRecordName"] : void 0;
  const conflictingRecordId = typeof data["conflictingRecordId"] === "string" ? data["conflictingRecordId"] : void 0;
  return { conflictingRecordId, conflictingRecordName, correlationId, errorCode, message, statusCode };
}

// src/api/api-client.ts
var BODY_METHODS = /* @__PURE__ */ new Set(["patch", "post", "put"]);
function methodExpectsBody(method) {
  return BODY_METHODS.has((method ?? "").toLowerCase());
}
var ApiClient = class {
  instance;
  correlationIdPrefix;
  logger;
  onRequestSent;
  onUnauthorized;
  tokenProvider;
  constructor(options) {
    const { config, correlationIdPrefix = "app", logger, onRequestSent, onUnauthorized, tokenProvider } = options;
    this.correlationIdPrefix = correlationIdPrefix;
    this.logger = logger;
    this.onRequestSent = onRequestSent;
    this.onUnauthorized = onUnauthorized;
    this.tokenProvider = tokenProvider;
    this.instance = axios.create({
      baseURL: config.baseURL,
      headers: {
        "Content-Type": "application/json",
        ...config.defaultHeaders
      },
      timeout: config.timeout ?? 1e4
    });
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }
  /**
   * Execute a request and return the response data.
   * Errors propagate naturally — error handling is done at the hook/component level.
   */
  async execute(call) {
    const response = await call();
    return response.data;
  }
  // ─── Private ─────────────────────────────────────────────────────────────
  /**
   * Makes a bodyless request one Fastify will actually accept. The instance stamps
   * `Content-Type: application/json` on every request, but axios sends NO body at all for a bodyless
   * call (`delete(url)`, `post(url)`), and Fastify rejects the two halves of that differently — so
   * neither half alone is a fix:
   *
   *   POST + content-type + no body  -> 400 "Body cannot be empty when content-type is set…"
   *   POST + no content-type         -> 415 Unsupported Media Type
   *   POST + content-type + `{}`     -> 200
   *   DELETE + no content-type       -> 200
   *
   * Hence the split: a body-carrying method gets an empty JSON body (keeping the header that now
   * describes it), while the rest drop the header that describes nothing. Left unhandled this 400s
   * before routing, which silently broke every bodyless DELETE and POST in the app — settings deletes,
   * vehicle restore, tenant impersonate. Requests that already carry data are untouched.
   */
  normalizeBodylessRequest(config) {
    if (config.data !== void 0 && config.data !== null) {
      return;
    }
    if (methodExpectsBody(config.method)) {
      config.data = {};
    } else {
      config.headers.delete("Content-Type");
    }
  }
  setupRequestInterceptor() {
    this.instance.interceptors.request.use(async (config) => {
      config._metadata = { startTime: Date.now() };
      const correlationId = generateCorrelationId(this.correlationIdPrefix);
      config.headers.set("x-correlation-id", correlationId);
      this.normalizeBodylessRequest(config);
      if (this.tokenProvider) {
        const token = await this.tokenProvider();
        if (token !== null) {
          config.headers.set("Authorization", `Bearer ${token}`);
        }
      }
      if (this.logger?.onRequest) {
        const method = config.method ?? "unknown";
        const url = config.url ?? "";
        this.logger.onRequest(method, url);
      }
      this.onRequestSent?.();
      return config;
    });
  }
  setupResponseInterceptor() {
    this.instance.interceptors.response.use(
      (response) => {
        this.logResponse(response);
        return response;
      },
      (error) => {
        this.logError(error);
        this.handleUnauthorized(error);
        return Promise.reject(error);
      }
    );
  }
  handleUnauthorized(error) {
    if (!this.onUnauthorized) {
      return;
    }
    const axiosError = error;
    if (axiosError.response?.status === 401) {
      this.onUnauthorized();
    }
  }
  logResponse(response) {
    const config = response.config;
    const durationMs = Date.now() - (config._metadata?.startTime ?? Date.now());
    if (this.logger?.onResponse) {
      const method = config.method ?? "unknown";
      const url = config.url ?? "";
      this.logger.onResponse(method, url, response.status, durationMs);
    }
  }
  extractErrorInfo(error) {
    const axiosError = error;
    return {
      durationMs: Date.now() - (axiosError.config?._metadata?.startTime ?? Date.now()),
      method: axiosError.config?.method ?? "unknown",
      status: axiosError.response?.status,
      url: axiosError.config?.url ?? ""
    };
  }
  logError(error) {
    if (!this.logger?.onError) {
      return;
    }
    const { durationMs, method, status, url } = this.extractErrorInfo(error);
    this.logger.onError(method, url, status, durationMs, error);
  }
};

// src/api/api-client-context.tsx
import { createContext, useContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
var ApiClientContext = createContext(null);
function ApiClientProvider({
  children,
  configs,
  correlationIdPrefix,
  logger,
  onRequestSent,
  onUnauthorized,
  tokenProvider
}) {
  const clients = useMemo(() => {
    return Object.fromEntries(
      Object.entries(configs).map(([name, config]) => [
        name,
        new ApiClient({ config, correlationIdPrefix, logger, onRequestSent, onUnauthorized, tokenProvider })
      ])
    );
  }, [configs, correlationIdPrefix, logger, onRequestSent, onUnauthorized, tokenProvider]);
  return /* @__PURE__ */ jsx(ApiClientContext.Provider, { value: clients, children });
}
function useApiClient(name) {
  const clients = useContext(ApiClientContext);
  if (!clients) {
    throw new Error("useApiClient must be used within <ApiClientProvider>");
  }
  const client = clients[name];
  if (!client) {
    throw new Error(`API client "${name}" not found. Available: ${Object.keys(clients).join(", ")}`);
  }
  return client;
}

// src/hooks/use-current-user-permissions.ts
var CURRENT_USER_PERMISSIONS_KEY = ["currentUser", "permissions"];
function useCurrentUserPermissions() {
  const client = useApiClient("admin-auth");
  return useQuery({
    gcTime: 10 * 6e4,
    // 10 min garbage collection
    queryFn: async () => {
      const response = await client.execute(
        () => client.instance.get("/users/me/permissions")
      );
      return response.data ?? [];
    },
    queryKey: [...CURRENT_USER_PERMISSIONS_KEY],
    staleTime: 5 * 6e4
    // 5 min stale time
  });
}
function hasPermission(permissions, permission) {
  return permissions?.includes(permission) ?? false;
}

// src/hooks/use-current-user.ts
import { useQuery as useQuery2 } from "@tanstack/react-query";
var currentUserKeys = {
  all: ["current-user"]
};
function useCurrentUser(isAuthenticated = true) {
  const client = useApiClient("admin-auth");
  const { data, isLoading } = useQuery2({
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await client.execute(
        () => client.instance.get("/users/me")
      );
      if (!response.data) {
        throw new Error("Unexpected empty response from /users/me");
      }
      return response.data;
    },
    queryKey: currentUserKeys.all,
    staleTime: 30 * 60 * 1e3
    // 30 minutes — user profile rarely changes mid-session
  });
  return {
    isLoading,
    user: data ?? null
  };
}

// src/hooks/use-debounced-value.ts
import { useEffect, useState } from "react";
function useDebouncedValue(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debouncedValue;
}

// src/hooks/use-search-page-state.ts
import { useEffect as useEffect2, useRef, useState as useState2 } from "react";
var DEFAULT_DEBOUNCE_MS = 300;
var DEFAULT_PAGE_SIZE2 = 10;
function useSearchPageState(config = {}) {
  const { debounceMs = DEFAULT_DEBOUNCE_MS, initialSort, pageSize = DEFAULT_PAGE_SIZE2 } = config;
  const [page, setPage] = useState2(1);
  const [searchInput, setSearchInput] = useState2("");
  const [sort, setSort] = useState2(initialSort);
  const debouncedSearch = useDebouncedValue(searchInput, debounceMs);
  const trimmedSearch = debouncedSearch.trim();
  const prevTrimmedSearchRef = useRef(trimmedSearch);
  let currentPage = page;
  if (prevTrimmedSearchRef.current !== trimmedSearch) {
    currentPage = 1;
  }
  useEffect2(() => {
    if (prevTrimmedSearchRef.current !== trimmedSearch) {
      prevTrimmedSearchRef.current = trimmedSearch;
      setPage(1);
    }
  }, [trimmedSearch]);
  function handleSort(sortBy, sortDirection) {
    setSort(buildSortParams(sortBy, sortDirection));
    setPage(1);
  }
  function handlePageChange(newPage) {
    setPage(newPage);
  }
  function resetPage() {
    setPage(1);
  }
  return {
    currentPage,
    debouncedSearch: trimmedSearch || void 0,
    onPageChange: handlePageChange,
    onSearchChange: setSearchInput,
    onSort: handleSort,
    pageSize,
    resetPage,
    searchValue: searchInput,
    sort,
    sortBy: sort?.sortBy,
    sortDirection: sort?.sortDirection
  };
}

// src/hooks/use-table-preferences.ts
import { useMutation, useQuery as useQuery3, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect as useEffect3, useMemo as useMemo2, useRef as useRef2 } from "react";

// src/hooks/table-preferences.api.ts
async function fetchTablePreferences(client) {
  const response = await client.execute(
    () => client.instance.get("/users/me/table-preferences")
  );
  return response.data ?? [];
}
async function saveTablePreference(client, tableId, value) {
  const response = await client.execute(
    () => client.instance.put(`/users/me/table-preferences/${tableId}`, value)
  );
  if (!response.data) {
    throw new Error(`Unexpected empty response from PUT /users/me/table-preferences/${tableId}`);
  }
  return response.data;
}

// src/hooks/use-table-preferences.ts
var tablePreferenceKeys = {
  all: ["table-preferences"]
};
var SAVE_DEBOUNCE_MS = 600;
function useTablePreferences(tableId, options) {
  const { enabled = true } = options ?? {};
  const client = useApiClient("admin-auth");
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery3({
    enabled,
    queryFn: () => fetchTablePreferences(client),
    queryKey: tablePreferenceKeys.all
  });
  const preference = useMemo2(() => data?.find((entry) => entry.tableId === tableId) ?? null, [data, tableId]);
  const mutation = useMutation({
    mutationFn: (value) => saveTablePreference(client, tableId, value),
    onError: (error) => {
      console.error(`Failed to save table preferences for "${tableId}"`, error);
      void queryClient.invalidateQueries({ queryKey: tablePreferenceKeys.all });
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(tablePreferenceKeys.all, (current) => {
        const existing = current ?? [];
        const index = existing.findIndex((entry) => entry.tableId === tableId);
        if (index === -1) {
          return [...existing, saved];
        }
        const next = [...existing];
        next[index] = saved;
        return next;
      });
    }
  });
  const mutateRef = useRef2(mutation.mutate);
  mutateRef.current = mutation.mutate;
  const timerRef = useRef2(null);
  useEffect3(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  const save = useCallback(
    (value) => {
      queryClient.setQueryData(tablePreferenceKeys.all, (current) => {
        const existing = current ?? [];
        const index = existing.findIndex((entry) => entry.tableId === tableId);
        if (index === -1) {
          const now = /* @__PURE__ */ new Date();
          const optimistic = {
            columnOrder: value.columnOrder,
            createdAt: now,
            deletedAt: null,
            hiddenColumns: value.hiddenColumns,
            id: `optimistic-${tableId}`,
            tableId,
            tenantId: "",
            updatedAt: now,
            userId: ""
          };
          return [...existing, optimistic];
        }
        const next = [...existing];
        next[index] = { ...next[index], columnOrder: value.columnOrder, hiddenColumns: value.hiddenColumns };
        return next;
      });
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        mutateRef.current(value);
      }, SAVE_DEBOUNCE_MS);
    },
    [queryClient, tableId]
  );
  return {
    isLoading,
    preference,
    save
  };
}

// src/hooks/use-date-range-display.ts
import { useTranslation } from "react-i18next";
function useDateRangeDisplay(from, to) {
  const { t } = useTranslation("common");
  if (from && to) return formatShortDateRange(from, to);
  if (from) return t("dates.from", { date: formatShortDate(from) });
  if (to) return t("dates.to", { date: formatShortDate(to) });
  return "\u2014";
}

// src/realtime/event-dispatcher.ts
var EventDispatcher = class {
  currentTenantId = null;
  nextId = 0;
  subscriptions = /* @__PURE__ */ new Map();
  setTenantId(tenantId) {
    this.currentTenantId = tenantId;
  }
  subscribe(options) {
    const id = `sub-${++this.nextId}`;
    this.subscriptions.set(id, { ...options, id });
    return id;
  }
  unsubscribe(id) {
    this.subscriptions.delete(id);
  }
  dispatch(event) {
    if (this.currentTenantId && event.tenantId && this.currentTenantId !== event.tenantId) {
      return;
    }
    for (const subscription of this.subscriptions.values()) {
      if (!this.matchesFilter(event, subscription)) {
        continue;
      }
      try {
        const result = subscription.handler(event);
        if (result != null && typeof result.catch === "function") {
          result.catch((error) => {
            console.error("[EventDispatcher] Async handler error:", error);
          });
        }
      } catch (error) {
        console.error("[EventDispatcher] Sync handler error:", error);
      }
    }
  }
  clear() {
    this.subscriptions.clear();
  }
  // ─── Private ─────────────────────────────────────────────────────────────
  matchesFilter(event, subscription) {
    return this.matchesCriteria(event.resource, subscription.resource) && this.matchesCriteria(event.operation, subscription.operation);
  }
  matchesCriteria(value, filter) {
    if (!filter) return true;
    if (Array.isArray(filter)) return filter.includes(value);
    return filter === value;
  }
};

// src/realtime/realtime-context.tsx
import { createContext as createContext2, useCallback as useCallback2, useContext as useContext2, useEffect as useEffect4, useMemo as useMemo3, useRef as useRef3, useState as useState3 } from "react";

// src/realtime/web-pubsub-service.ts
var DEFAULT_MAX_RECONNECT_ATTEMPTS = 5;
var DEFAULT_RECONNECT_INTERVAL_MS = 5e3;
var DEFAULT_HEARTBEAT_INTERVAL_MS = 3e4;
var WebPubSubService = class {
  client = null;
  config = null;
  connectionStateHandlers = /* @__PURE__ */ new Set();
  dispatcher = new EventDispatcher();
  error = null;
  heartbeatTimer;
  reconnectAttempts = 0;
  state = "disconnected";
  getState() {
    return this.state;
  }
  getError() {
    return this.error;
  }
  /**
   * Connect to Azure Web PubSub.
   */
  async connect(config, apiInstance, tenantId, userId) {
    this.config = config;
    this.setState("connecting");
    let WebPubSubClient;
    try {
      const module = await import("@azure/web-pubsub-client");
      WebPubSubClient = module.WebPubSubClient;
    } catch {
      const message = "@azure/web-pubsub-client is not installed. Install it to enable realtime features.";
      this.setState("error", new Error(message));
      return;
    }
    try {
      const clientUrl = await this.resolveClientUrl(config, apiInstance, tenantId, userId);
      this.dispatcher.setTenantId(tenantId);
      const pubsubClient = new WebPubSubClient(clientUrl);
      pubsubClient.on("group-message", (msg) => {
        this.handleMessage(msg);
      });
      pubsubClient.on("connected", () => {
        this.reconnectAttempts = 0;
        this.setState("connected");
        this.startHeartbeat();
      });
      pubsubClient.on("disconnected", () => {
        this.stopHeartbeat();
        this.attemptReconnect();
      });
      this.client = pubsubClient;
      await pubsubClient.start();
    } catch (error) {
      const connectError = error instanceof Error ? error : new Error("Connection failed");
      this.setState("error", connectError);
    }
  }
  disconnect() {
    this.stopHeartbeat();
    const client = this.client;
    if (client?.stop) {
      client.stop();
    }
    this.client = null;
    this.dispatcher.clear();
    this.reconnectAttempts = 0;
    this.setState("disconnected");
  }
  subscribe(options) {
    return this.dispatcher.subscribe(options);
  }
  unsubscribe(id) {
    this.dispatcher.unsubscribe(id);
  }
  onConnectionStateChanged(handler) {
    this.connectionStateHandlers.add(handler);
    return () => {
      this.connectionStateHandlers.delete(handler);
    };
  }
  // ─── Private ─────────────────────────────────────────────────────────────
  async resolveClientUrl(config, apiInstance, tenantId, userId) {
    if (config.clientUrl) {
      return config.clientUrl;
    }
    if (!config.negotiateUrl) {
      throw new Error("[WebPubSubService] Either clientUrl or negotiateUrl must be provided in RealtimeConfig");
    }
    const response = await apiInstance.post(config.negotiateUrl, { tenantId, userId });
    return response.data.url;
  }
  handleMessage(message) {
    try {
      const event = message.data;
      if (event && typeof event === "object" && "resource" in event && "operation" in event) {
        this.dispatcher.dispatch(event);
      }
    } catch (error) {
      console.error("[WebPubSubService] Failed to handle message:", error);
    }
  }
  attemptReconnect() {
    const maxAttempts = this.config?.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS;
    const intervalMs = this.config?.reconnectIntervalMs ?? DEFAULT_RECONNECT_INTERVAL_MS;
    if (this.reconnectAttempts >= maxAttempts) {
      this.setState("error", new Error(`[WebPubSubService] Max reconnect attempts (${maxAttempts}) reached`));
      return;
    }
    this.reconnectAttempts++;
    this.setState("reconnecting");
    setTimeout(() => {
      const client = this.client;
      if (client?.start) {
        client.start().catch((error) => {
          console.error("[WebPubSubService] Reconnect failed:", error);
          this.attemptReconnect();
        });
      }
    }, intervalMs);
  }
  startHeartbeat() {
    const intervalMs = this.config?.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS;
    this.heartbeatTimer = setInterval(() => {
      if (this.state !== "connected") {
        this.stopHeartbeat();
      }
    }, intervalMs);
  }
  stopHeartbeat() {
    if (this.heartbeatTimer !== void 0) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = void 0;
    }
  }
  setState(state, error) {
    this.state = state;
    this.error = error ?? null;
    for (const handler of this.connectionStateHandlers) {
      handler(state, error);
    }
  }
};

// src/realtime/realtime-context.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var RealtimeContext = createContext2(null);
function RealtimeProvider({
  apiClientName,
  autoConnect = true,
  children,
  config,
  enabled = false,
  featureFlags = {},
  tenantId,
  userId
}) {
  const apiClient = useApiClient(apiClientName);
  const serviceRef = useRef3(new WebPubSubService());
  const [connectionState, setConnectionState] = useState3("disconnected");
  const [connectionError, setConnectionError] = useState3(null);
  useEffect4(() => {
    const service = serviceRef.current;
    const unsubscribe = service.onConnectionStateChanged((state, error) => {
      setConnectionState(state);
      setConnectionError(error ?? null);
    });
    return unsubscribe;
  }, []);
  useEffect4(() => {
    const service = serviceRef.current;
    const shouldConnect = enabled && autoConnect && !!config && !!tenantId && !!userId && connectionState === "disconnected";
    if (!shouldConnect) return;
    service.connect(config, apiClient.instance, tenantId, userId).catch((error) => {
      console.error("[RealtimeProvider] Connection error:", error);
    });
  }, [enabled, autoConnect, config, tenantId, userId, apiClient, connectionState]);
  useEffect4(() => {
    const service = serviceRef.current;
    return () => {
      service.disconnect();
    };
  }, []);
  const connect = useCallback2(async () => {
    if (!config || !tenantId || !userId) return;
    await serviceRef.current.connect(config, apiClient.instance, tenantId, userId);
  }, [config, tenantId, userId, apiClient]);
  const disconnect = useCallback2(() => {
    serviceRef.current.disconnect();
  }, []);
  const isConnected = connectionState === "connected";
  const value = useMemo3(
    () => ({
      connect,
      disconnect,
      error: connectionError,
      featureFlags,
      isConnected,
      isEnabled: enabled,
      service: serviceRef.current,
      state: connectionState
    }),
    [connect, disconnect, connectionError, featureFlags, isConnected, enabled, connectionState]
  );
  return /* @__PURE__ */ jsx2(RealtimeContext.Provider, { value, children });
}
function useRealtimeContext() {
  const context = useContext2(RealtimeContext);
  if (!context) {
    return {
      connect: async () => {
      },
      disconnect: () => {
      },
      error: null,
      featureFlags: {},
      isConnected: false,
      isEnabled: false,
      state: "disconnected"
    };
  }
  return context;
}
function useInternalRealtimeContext() {
  return useContext2(RealtimeContext);
}

// src/realtime/use-realtime.ts
import { useEffect as useEffect5, useRef as useRef4, useState as useState4 } from "react";
function useRealtimeSubscription(options) {
  const context = useInternalRealtimeContext();
  const [subscriptionId, setSubscriptionId] = useState4(null);
  const handlerRef = useRef4(options.handler);
  handlerRef.current = options.handler;
  const isEnabled = context?.isEnabled ?? false;
  const isConnected = context?.isConnected ?? false;
  const service = context?.service ?? null;
  const enabled = options.enabled !== false;
  const shouldSubscribe = isEnabled && isConnected && enabled;
  const resource = options.resource;
  const operation = options.operation;
  useEffect5(() => {
    if (!shouldSubscribe || !service) {
      setSubscriptionId(null);
      return;
    }
    const id = service.subscribe({
      handler: (event) => handlerRef.current(event),
      operation,
      resource
    });
    setSubscriptionId(id);
    return () => {
      service.unsubscribe(id);
      setSubscriptionId(null);
    };
  }, [shouldSubscribe, service, resource, operation]);
  return {
    isSubscribed: subscriptionId !== null,
    subscriptionId
  };
}
function useRealtimeState() {
  const { error, isConnected, isEnabled, state } = useRealtimeContext();
  return { error, isConnected, isEnabled, state };
}
function useRealtimeFeatureFlag(featureName) {
  const { featureFlags, isEnabled } = useRealtimeContext();
  return isEnabled && (featureFlags[featureName] ?? false);
}

// src/permissions/permissions-context.tsx
import { createContext as createContext3, useCallback as useCallback3, useContext as useContext3, useEffect as useEffect6, useMemo as useMemo4 } from "react";
import { useQuery as useQuery4, useQueryClient as useQueryClient2 } from "@tanstack/react-query";

// src/permissions/permissions-api.ts
function createPermissionsApi(client) {
  return {
    fetchMyPermissions: async () => {
      const response = await client.execute(
        () => client.instance.get("/users/me/permissions")
      );
      return response.data ?? [];
    }
  };
}

// src/permissions/permissions-keys.ts
var permissionKeys = {
  all: ["permissions"],
  mine: () => [...permissionKeys.all, "me"]
};

// src/permissions/permissions-context.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var PermissionsContext = createContext3(null);
function PermissionsProviderCore({
  children,
  isAuthenticated,
  loadPermissions
}) {
  const queryClient = useQueryClient2();
  const {
    data: permissions = [],
    isFetched,
    isLoading
  } = useQuery4({
    enabled: isAuthenticated,
    queryFn: loadPermissions,
    queryKey: permissionKeys.mine()
  });
  useEffect6(() => {
    if (!isAuthenticated) {
      queryClient.removeQueries({ queryKey: permissionKeys.all });
    }
  }, [isAuthenticated, queryClient]);
  const effectivePermissions = isAuthenticated ? permissions : [];
  const effectiveIsLoading = isAuthenticated ? isLoading : false;
  const effectiveIsReady = isAuthenticated && isFetched;
  const permissionsSet = useMemo4(() => new Set(effectivePermissions), [effectivePermissions]);
  const hasPermission2 = useCallback3((permission) => permissionsSet.has(permission), [permissionsSet]);
  const hasAllPermissions = useCallback3(
    (perms) => perms.every((p) => permissionsSet.has(p)),
    [permissionsSet]
  );
  const hasAnyPermission = useCallback3(
    (perms) => perms.some((p) => permissionsSet.has(p)),
    [permissionsSet]
  );
  const value = useMemo4(
    () => ({
      hasAllPermissions,
      hasAnyPermission,
      hasPermission: hasPermission2,
      isLoading: effectiveIsLoading,
      isReady: effectiveIsReady,
      permissions: effectivePermissions
    }),
    [hasAllPermissions, hasAnyPermission, hasPermission2, effectiveIsLoading, effectiveIsReady, effectivePermissions]
  );
  return /* @__PURE__ */ jsx3(PermissionsContext.Provider, { value, children });
}
function PermissionsProviderWithApi({ children, isAuthenticated }) {
  const client = useApiClient("admin-auth");
  const api = useMemo4(() => createPermissionsApi(client), [client]);
  const loadPermissions = useCallback3(() => api.fetchMyPermissions(), [api]);
  return /* @__PURE__ */ jsx3(PermissionsProviderCore, { isAuthenticated, loadPermissions, children });
}
function PermissionsProvider({
  children,
  isAuthenticated = true,
  loadPermissions: loadPermissionsOverride
}) {
  if (loadPermissionsOverride) {
    return /* @__PURE__ */ jsx3(PermissionsProviderCore, { isAuthenticated, loadPermissions: loadPermissionsOverride, children });
  }
  return /* @__PURE__ */ jsx3(PermissionsProviderWithApi, { isAuthenticated, children });
}
function usePermissions() {
  const context = useContext3(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a <PermissionsProvider>");
  }
  return context;
}

// src/permissions/require-permission.tsx
import { Fragment, jsx as jsx4 } from "react/jsx-runtime";
function warnInvalidPropCount(providedCount) {
  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
    console.warn(
      `[RequirePermission] Exactly one of \`permission\`, \`allOf\`, or \`anyOf\` must be provided. Received ${providedCount} prop(s). Rendering fallback (fail-closed).`
    );
  }
}
function warnEmptyArray() {
  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
    console.warn(
      "[RequirePermission] `allOf` and `anyOf` must be non-empty arrays. Received an empty array. Rendering fallback (fail-closed)."
    );
  }
}
function resolveAccess(permission, allOf, anyOf, hasPermission2, hasAllPermissions, hasAnyPermission) {
  if (permission !== void 0) {
    return hasPermission2(permission);
  }
  if (allOf !== void 0) {
    return hasAllPermissions(allOf);
  }
  return hasAnyPermission(anyOf);
}
function RequirePermission({
  allOf,
  anyOf,
  children,
  fallback = null,
  permission
}) {
  const { hasAllPermissions, hasAnyPermission, hasPermission: hasPermission2, isLoading } = usePermissions();
  if (isLoading) {
    return /* @__PURE__ */ jsx4(Fragment, { children: fallback });
  }
  const providedCount = [permission, allOf, anyOf].filter((p) => p !== void 0).length;
  if (providedCount !== 1) {
    warnInvalidPropCount(providedCount);
    return /* @__PURE__ */ jsx4(Fragment, { children: fallback });
  }
  if (allOf !== void 0 && allOf.length === 0 || anyOf !== void 0 && anyOf.length === 0) {
    warnEmptyArray();
    return /* @__PURE__ */ jsx4(Fragment, { children: fallback });
  }
  const hasAccess = resolveAccess(permission, allOf, anyOf, hasPermission2, hasAllPermissions, hasAnyPermission);
  return /* @__PURE__ */ jsx4(Fragment, { children: hasAccess ? children : fallback });
}
export {
  ApiClient,
  ApiClientProvider,
  ApiError,
  DEFAULT_GC_TIME_MS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_STALE_TIME_MS,
  EventDispatcher,
  LANGUAGE_LABELS,
  LOCALE_MAP,
  PermissionsProvider,
  RTL_LANGUAGES,
  RealtimeProvider,
  RequirePermission,
  SEARCH_DEBOUNCE_MS,
  WebPubSubService,
  areAllRequirementsMet,
  buildSortParams,
  createApiLogger,
  formatDateTimeMedium,
  formatDisplayDate,
  formatIsoDate,
  formatLongDate,
  formatRelativeDate,
  formatShortDate,
  formatShortDateRange,
  formatTimeWindow,
  generateCorrelationId,
  getApiData,
  getApiErrorDetail,
  getPasswordRequirements,
  hasPermission,
  isApiError,
  mapPaginatedResponse,
  resolveLocale,
  tablePreferenceKeys,
  toPaginatedResponse,
  toQueryParams,
  useApiClient,
  useCurrentUser,
  useCurrentUserPermissions,
  useDateRangeDisplay,
  useDebouncedValue,
  usePermissions,
  useRealtimeContext,
  useRealtimeFeatureFlag,
  useRealtimeState,
  useRealtimeSubscription,
  useSearchPageState,
  useTablePreferences
};
//# sourceMappingURL=index.mjs.map